"use client"
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from "@/lib/utils";
import SharedHero from '../ui/shared-hero';
import { BankersVisuals } from './use-case-elements/BankersVisuals';
import { BusinessVisuals } from './use-case-elements/BusinessVisuals';
import { SalesVisuals } from './use-case-elements/SalesVisuals';
import { StartupsVisuals } from './use-case-elements/StartupsVisuals';
import { StudentsVisuals } from './use-case-elements/StudentsVisuals';

// import { CheckCircle2 } from 'lucide-react';

const useCases = [
    {
        id: "startups",
        label: "Startups & Founders",
        title: "Everything You Need To Go From Idea To Investor-Ready",
        description: "Deckwale is designed with startup workflows in mind. From early ideation to fundraising and stakeholder communication, founders can generate structured, professional documents without spending days on slides.",
        commonUseCases: [
            "Fundraising pitch decks (pre-seed to Series A+)",
            "Cap table structures and equity breakdowns",
            "Investor updates and progress reports",
            "Team introduction decks",
            "Market and competitor analysis summaries"
        ],
        features: [
            "AI understands investor expectations and deck flow",
            "Built-in prompts for traction, market size, and storytelling",
            "Faster iteration before investor meetings"
        ],
        tagline: "Spend less time formatting slides and more time building your startup.",
        image: "https://images.unsplash.com/photo-1559136555-930d72f1d30c?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "businesses",
        label: "Businesses & Enterprises",
        title: "Professional Documents At Scale — Without Design Bottlenecks",
        description: "Deckwale helps growing businesses and large teams standardize how they create and share presentations, reports, and proposals. AI ensures consistency, clarity, and speed across departments.",
        commonUseCases: [
            "Business reports and executive summaries",
            "Internal strategy and planning presentations",
            "Client-facing brochures and proposals",
            "Quarterly and annual performance decks",
            "Cross-team presentations with consistent branding"
        ],
        features: [
            "Structured content generation for repeatable workflows",
            "Templates designed for real business scenarios",
            "Faster collaboration and easier updates"
        ],
        tagline: "Create business-ready documents without relying on design or ops teams.",
        image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "sales",
        label: "Sales & GTM Teams",
        title: "Create Sales Collateral That Converts",
        description: "Deckwale helps sales and business development teams generate persuasive, professional documents that communicate value clearly and close deals faster.",
        commonUseCases: [
            "Sales pitch presentations",
            "Partnership proposals",
            "Client-specific business decks",
            "Product and solution brochures",
            "Proposal follow-ups and summaries"
        ],
        features: [
            "AI structures content around benefits and outcomes",
            "Faster turnaround for custom client decks",
            "Clean, professional layouts that build credibility"
        ],
        tagline: "Spend more time selling and less time building slides.",
        image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "students",
        label: "Students & Early Professionals",
        title: "Create Polished Presentations Without Design Skills",
        description: "Deckwale isn’t just for businesses — it’s also a powerful tool for students and early-career professionals who want their work to stand out.",
        commonUseCases: [
            "Academic presentations and reports",
            "Project submissions and case studies",
            "Startup pitch competitions",
            "Internship and placement presentations",
            "Team project decks"
        ],
        features: [
            "Simple prompts generate structured presentations",
            "Clean layouts without manual design work",
            "Helps focus on content and storytelling"
        ],
        tagline: "Present ideas clearly and confidently, even with limited experience.",
        image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "investment-bankers",
        label: "Investment Bankers & Advisory Firms",
        title: "Create Client-Ready Pitch Books and Financial Presentations Faster",
        description: "Deckwale helps investment bankers and advisory teams produce high-quality pitch books, client presentations, and analytical documents with speed and consistency. From deal sourcing to client meetings, generate structured, professional documents without repetitive manual work.",
        commonUseCases: [
            "Client pitch books and mandate presentations",
            "M&A and strategic advisory decks",
            "Industry overviews and market landscape reports",
            "Company profiles and investment memorandums",
            "Management presentations and deal updates"
        ],
        features: [
            "Automated industry overviews and market analysis",
            "Consistent formatting for financial presentations",
            "Rapid iteration for deal updates and mandates"
        ],
        tagline: "Deliver polished, professional presentations on tighter timelines — without increasing workload.",
        image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=2000"
    }
];

const UseCases = () => {
    const [activeTab, setActiveTab] = useState(useCases[0].id);

    return (
        <div className="min-h-screen font-jakarta bg-white">
            <SharedHero
                label="Use Cases"
                title="Built For Every Team"
                subtitle="Deckwale helps startups, businesses, and teams create professional presentations and business documents faster using AI."
            />

            {/* Main Content Area with Fixed Margins */}
            {/* Added -mt-12 to pull content slightly up into the hero space or close gap */}
            <div className="mx-4 md:mx-[80px] -mt-12 relative z-20">
                {/* Tabs */}
                <div className="flex items-end pl-1 md:pl-4 gap-1 overflow-x-auto no-scrollbar mask-gradient-r">
                    {useCases.map((useCase) => (
                        <button
                            key={useCase.id}
                            onClick={() => setActiveTab(useCase.id)}
                            className={cn(
                                "relative px-5 py-3 md:px-8 md:py-4 rounded-t-xl font-syne font-bold text-sm tracking-wide transition-all duration-300 whitespace-nowrap shrink-0",
                                activeTab === useCase.id
                                    ? "bg-[#F8FBFF] text-[#2563EB] z-10 translate-y-[1px] shadow-sm" // Matches content bg
                                    : "bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                            )}
                        >
                            {useCase.label}
                            {activeTab === useCase.id && (
                                <div className="absolute top-0 left-0 w-full h-[3px] bg-[#2563EB] rounded-t-xl" />
                            )}
                        </button>
                    ))}
                </div>

                {/* Content Card - Height Fits Content */}
                <div className="bg-[#F8FBFF] rounded-b-2xl rounded-tr-2xl rounded-tl-none border border-gray-100 overflow-hidden shadow-xl shadow-blue-900/5 h-auto relative z-0">
                    <AnimatePresence mode="wait">
                        {useCases.map((useCase) => (
                            activeTab === useCase.id && (
                                <motion.div
                                    key={useCase.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                    className="flex flex-col md:flex-row h-full"
                                >
                                    {/* Text Content */}
                                    <div className="flex-1 p-6 md:p-14 flex flex-col h-full overflow-y-auto custom-scrollbar">
                                        <motion.h2
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.1 }}
                                            className="font-syne font-bold text-3xl md:text-4xl text-black mb-6 leading-tight capitalize"
                                        >
                                            {useCase.title}
                                        </motion.h2>
                                        <motion.p
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.2 }}
                                            className="text-gray-600 text-[15px] leading-relaxed mb-8 max-w-xl"
                                        >
                                            {useCase.description}
                                        </motion.p>

                                        {/* Single Column for Use Cases */}
                                        <div className="mb-8">
                                            <h3 className="font-syne font-bold text-sm uppercase tracking-wider text-gray-400 mb-4">Common Use Cases</h3>
                                            <ul className="space-y-3">
                                                {useCase.commonUseCases.map((item, i) => (
                                                    <motion.li
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: 0.3 + (i * 0.05) }}
                                                        key={i}
                                                        className="flex items-start gap-2.5 text-[14px] text-gray-700 font-medium"
                                                    >
                                                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#2563EB] shrink-0" />
                                                        {item}
                                                    </motion.li>
                                                ))}
                                            </ul>
                                        </div>

                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.6 }}
                                            className="mt-auto pt-6 border-t border-blue-100"
                                        >
                                            <p className="text-[#2563EB] font-syne font-semibold text-lg italic">
                                                "{useCase.tagline}"
                                            </p>
                                        </motion.div>
                                    </div>

                                    {/* Visual Content - Full Height */}
                                    <div className="hidden md:block w-[45%] h-auto relative overflow-hidden bg-gray-50 border-l border-white/50">
                                        {useCase.id === "startups" && <StartupsVisuals />}
                                        {useCase.id === "businesses" && <BusinessVisuals />}
                                        {useCase.id === "sales" && <SalesVisuals />}
                                        {useCase.id === "students" && <StudentsVisuals />}
                                        {useCase.id === "investment-bankers" && <BankersVisuals />}
                                    </div>
                                </motion.div>
                            )
                        ))}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default UseCases;
